import classNames from 'classnames';
import { type ReactNode, useId } from 'react';
import type { To } from 'react-router';
import styled from 'styled-components';

import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';

const ORB_SIZE = 56;
const RADIUS = ORB_SIZE / 2;
const ARC_FONT_SIZE = 8;
const ARC_CAP_HEIGHT = ARC_FONT_SIZE * 0.7;
const TOP_TEXT_RADIUS = RADIUS - 11;
const BOTTOM_TEXT_RADIUS = TOP_TEXT_RADIUS + ARC_CAP_HEIGHT;

const Root = styled(ButtonBase)`
  position: relative;
  isolation: isolate;
  display: inline-flex;
`;

const GlowCircle = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${ORB_SIZE}px;
  height: ${ORB_SIZE}px;
  border-radius: 50%;
  background: rgba(14, 14, 14, 0.54);
  backdrop-filter: blur(20px);
  box-shadow:
    0px 0px 20px 0px rgba(var(--orb-color), 0.6),
    inset 0px 0px 32px 0px rgba(var(--orb-color), 0.3),
    inset 0px 0px 8px 0px rgba(var(--orb-color), 0.4),
    inset 0px 0px 4px 1px rgba(var(--orb-color), 0.8);
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    padding: 1px;
    background: linear-gradient(
      to bottom,
      rgba(var(--c-rgb-white), 0.5),
      rgba(var(--c-rgb-white), 0)
    );
    mask:
      linear-gradient(#fff, #fff) content-box,
      linear-gradient(#fff, #fff);
    mask-composite: exclude;
    pointer-events: none;
  }
`;

const ArcLabel = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  text {
    font: var(--t-bold) ${ARC_FONT_SIZE}px / 1 var(--sorare-font);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    fill: color-mix(in srgb, rgb(var(--orb-color)) 35%, white);
    filter: drop-shadow(0 0 2px rgba(var(--orb-color), 0.9));
  }
`;

const Icon = styled.span`
  display: flex;
  color: var(--c-white);
`;

type Props = {
  topLabel?: ReactNode;
  bottomLabel?: ReactNode;
  colorRgb: string;
  children: ReactNode;
  onClick?: () => void;
  to?: To;
};

export const LiveOrb = ({
  topLabel,
  bottomLabel,
  colorRgb,
  children,
  onClick,
  to,
}: Props) => {
  const topArcId = useId();
  const bottomArcId = useId();

  return (
    <Root
      {...(to ? { to } : { onClick })}
      className={classNames('LiveOrb')}
      style={{ '--orb-color': colorRgb } as React.CSSProperties}
    >
      <GlowCircle>
        <ArcLabel viewBox={`0 0 ${ORB_SIZE} ${ORB_SIZE}`} aria-hidden="true">
          <defs>
            <path
              id={topArcId}
              fill="none"
              d={`M ${RADIUS - TOP_TEXT_RADIUS} ${RADIUS}
                  A ${TOP_TEXT_RADIUS} ${TOP_TEXT_RADIUS} 0 0 1 ${RADIUS + TOP_TEXT_RADIUS} ${RADIUS}`}
            />
            <path
              id={bottomArcId}
              fill="none"
              d={`M ${RADIUS - BOTTOM_TEXT_RADIUS} ${RADIUS}
                  A ${BOTTOM_TEXT_RADIUS} ${BOTTOM_TEXT_RADIUS} 0 0 0 ${RADIUS + BOTTOM_TEXT_RADIUS} ${RADIUS}`}
            />
          </defs>
          {topLabel != null && (
            <text textAnchor="middle">
              <textPath href={`#${topArcId}`} startOffset="50%">
                {topLabel}
              </textPath>
            </text>
          )}
          {bottomLabel != null && (
            <text textAnchor="middle">
              <textPath href={`#${bottomArcId}`} startOffset="50%">
                {bottomLabel}
              </textPath>
            </text>
          )}
        </ArcLabel>
        <Icon>{children}</Icon>
      </GlowCircle>
    </Root>
  );
};
